import React from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

export interface Batch {
    id: string;
    quantity: string;
    deliveryDate: string;
}

interface Props {
    batches: Batch[];
    partQuantity: number;
    partId: string;
    showErrors?: boolean;
    onAdd: () => void;
    onChange: (batchId: string, field: string, value: string) => void;
    onDelete: (batchId: string) => void;
}

const BatchSection: React.FC<Props> = ({
    batches,
    onAdd,
    partId,
    onChange,
    onDelete,
    partQuantity,
    showErrors
}) => {
    const [openConfirm, setOpenConfirm] = React.useState(false);
    const [selectedBatchId, setSelectedBatchId] = React.useState<string | null>(
        null
    );

    const [quantityError, setQuantityError] = React.useState<string | null>(
        null
    );

    const totalBatchQty = batches.reduce(
        (sum, b) => sum + Number(b.quantity || 0),
        0
    );

    return (
        <>
            <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                mb={2}
            >
                {/* <Typography variant="body1" mt={2} mb={2} fontWeight={"bold"}>
          Batches
        </Typography> */}
                <Box>
                    <Typography variant="body1" fontWeight={"bold"} mt={2}>
                        Batches
                    </Typography>
                    <Typography
                        variant="caption"
                        color={
                            totalBatchQty !== partQuantity ? "error" : "success"
                        }
                    >
                        Total quantity: {totalBatchQty} / {partQuantity} (
                        {Math.max(0, partQuantity - totalBatchQty)})
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {totalBatchQty >= partQuantity &&
                        batches.some((b) => !b.quantity) && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                    batches.forEach((b) => {
                                        if (!b.quantity) onDelete(b.id);
                                    });
                                }}
                            >
                                Remove Empty Batches
                            </Button>
                        )}
                    <Button
                        variant="contained"
                        // sx={{ mb: 2 }}
                        onClick={onAdd}
                        disabled={totalBatchQty >= partQuantity}
                    >
                        + ADD BATCH
                    </Button>
                </Box>
            </Box>
            <Box>
                {batches.map((b, i) => (
                    <Box
                        key={b.id}
                        sx={{
                            border: "1px solid #ddd",
                            borderRadius: 2,
                            p: 2,
                            mb: 2
                        }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={1}
                            alignItems="center"
                        >
                            <div>
                                <Typography width={"100%"}>
                                    Batch 0{i + 1}{" "}
                                    <b style={{ fontSize: "12px" }}>
                                        ({partId})
                                    </b>
                                </Typography>
                            </div>

                            <IconButton
                                color="error"
                                onClick={() => {
                                    setSelectedBatchId(b.id);
                                    setOpenConfirm(true);
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Quantity"
                                    fullWidth
                                    value={b.quantity}
                                    error={
                                        !!quantityError ||
                                        (showErrors && !b.quantity)
                                    }
                                    helperText={
                                        quantityError ||
                                        (showErrors && !b.quantity
                                            ? "Required"
                                            : "")
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setQuantityError(null);

                                        if (value === "") {
                                            onChange(b.id, "quantity", "");
                                            return;
                                        }

                                        if (!/^\d+(\.\d{0,2})?$/.test(value))
                                            return;

                                        const newQty = Number(value);

                                        // calculate total excluding current batch
                                        const otherBatchesTotal =
                                            batches.reduce(
                                                (sum, batch) =>
                                                    batch.id === b.id
                                                        ? sum
                                                        : sum +
                                                          Number(
                                                              batch.quantity ||
                                                                  0
                                                          ),
                                                0
                                            );

                                        // ❌ block if exceeds part quantity

                                        if (
                                            otherBatchesTotal + newQty >
                                            partQuantity
                                        ) {
                                            setQuantityError(
                                                `Total batch quantity cannot exceed ${partQuantity}`
                                            );
                                            return;
                                        }

                                        onChange(b.id, "quantity", value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        sx={{ minWidth: "100%" }}
                                        label="Delivery Date"
                                        minDate={dayjs()}
                                        value={
                                            b.deliveryDate
                                                ? dayjs(b.deliveryDate)
                                                : null
                                        }
                                        onChange={(newValue) =>
                                            onChange(
                                                b.id,
                                                "deliveryDate",
                                                newValue
                                                    ? newValue.format(
                                                          "YYYY-MM-DD"
                                                      )
                                                    : ""
                                            )
                                        }
                                        slotProps={{
                                            textField: {
                                                error:
                                                    showErrors &&
                                                    !b.deliveryDate,
                                                helperText:
                                                    showErrors &&
                                                    !b.deliveryDate
                                                        ? "Required"
                                                        : "",
                                                fullWidth: true,
                                            },
                                            field: {
                                                readOnly: true 
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Box>

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirmation</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Are you sure want to delete the Batch?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button color="error" onClick={() => setOpenConfirm(false)}>
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        onClick={() => {
                            if (selectedBatchId) {
                                onDelete(selectedBatchId);
                            }
                            setOpenConfirm(false);
                            setSelectedBatchId(null);
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BatchSection;
